import { type ReactNode, useId } from "react";
import { cn } from "~/foundation/utils/cn";

/**
 * The inspectors' form vocabulary.
 *
 * kms's `parts.ts` is styled-components and is deliberately NOT ported; this is the same set of
 * shapes in slate's idiom — Tailwind, logical properties, slate's tokens. It exists because the
 * five inspector tabs are roughly a hundred labelled controls between them, and a hundred
 * hand-rolled `<label>`/`<input>` pairs is a hundred chances for the Hebrew face to get a
 * physically-directional margin.
 */

export function Group({
  title,
  hint,
  children,
  actions,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2 border-divider border-t pt-3 first:border-t-0 first:pt-0">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-medium text-small text-text-secondary uppercase tracking-wide">
          {title}
        </h3>
        {actions}
      </div>
      {hint ? <p className="text-small text-text-tertiary">{hint}</p> : null}
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function Shell({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={htmlFor} className="font-medium text-small text-text-secondary">
        {label}
      </label>
      {children}
      {error ? <p className="text-danger text-small">{error}</p> : null}
      {hint && !error ? <p className="text-small text-text-tertiary">{hint}</p> : null}
    </div>
  );
}

const CONTROL =
  "w-full rounded-sm border border-input-border bg-bg px-2 py-1.5 text-body text-text " +
  "focus:border-input-border-focus focus:outline-none disabled:opacity-60";

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  error,
  mono,
  disabled,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  mono?: boolean;
  disabled?: boolean;
  type?: "text" | "number";
}) {
  const id = useId();
  return (
    <Shell label={label} hint={hint} error={error} htmlFor={id}>
      <input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn(CONTROL, "h-8 py-0", mono && "font-mono text-small")}
      />
    </Shell>
  );
}

export function AreaField({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
  hint,
  error,
  mono,
  spellCheck = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  hint?: string;
  error?: string;
  mono?: boolean;
  spellCheck?: boolean;
}) {
  const id = useId();
  return (
    <Shell label={label} hint={hint} error={error} htmlFor={id}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        spellCheck={spellCheck}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn(CONTROL, mono && "font-mono text-small")}
      />
    </Shell>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  hint?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <Shell label={label} hint={hint} htmlFor={id}>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={cn(CONTROL, "h-8 py-0")}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Shell>
  );
}

export function CheckField({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="flex items-center gap-2 text-body text-text">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="size-4 accent-accent"
        />
        {label}
      </label>
      {hint ? <p className="ms-6 text-small text-text-tertiary">{hint}</p> : null}
    </div>
  );
}

/** A clickable reference — a node id, a source token — that selects what it names. */
export function RefChip({
  children,
  onClick,
  tone = "neutral",
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  tone?: "neutral" | "good" | "bad";
  title?: string;
}) {
  const className = cn(
    "inline-flex max-w-full items-center truncate rounded-sm px-1.5 py-0.5 font-mono text-small",
    tone === "good" && "bg-success/16 text-text",
    tone === "bad" && "bg-danger/14 text-danger",
    tone === "neutral" && "bg-bg-secondary text-text-secondary",
    onClick && "cursor-pointer hover:opacity-80",
  );
  if (!onClick) {
    return (
      <span className={className} title={title}>
        {children}
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} title={title}>
      {children}
    </button>
  );
}

/** A short explanatory strip. `tone` carries the meaning; the words carry the reason. */
export function Note({
  tone = "info",
  children,
}: {
  tone?: "info" | "warn" | "danger";
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "rounded-sm border-s-2 px-2 py-1.5 text-small",
        tone === "info" && "border-s-accent bg-accent/8 text-text-secondary",
        tone === "warn" && "border-s-warning bg-warning/10 text-text-secondary",
        tone === "danger" && "border-s-danger bg-danger/10 text-danger",
      )}
    >
      {children}
    </p>
  );
}
